/* src/components/projects/ScaffoldCanvas.jsx */
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Collapse,
  Select,
  Input,
  Button,
  Row,
  Col,
  Typography,
  Tooltip,
  Space,
  Spin,
  Image as AntImage,
  Modal,
  message as antdMessage,
} from "antd";
import {
  EyeOutlined,
  EditOutlined,
  DownOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

import ChemDrawCanvas from "../shared/ChemDrawCanvas";
import { generateImagesFromSmiles } from "../../util/ChemDrawUtils";
import { HELPER } from "../../constants/labels";
import { isValidScaffoldSmiles } from "../../util/helper";

const { Panel } = Collapse;
const { Text } = Typography;
const { Option } = Select;

const HISTORY_LIMIT = 50;

/* helper to generate 2D PNG with the API */
const getImage = async (smiles, setState) => {
  const trimmed = smiles?.trim();
  if (!trimmed) {
    antdMessage.warning("Please enter a SMILES string first.");
    return;
  }
  if (trimmed.length < 3) {
    antdMessage.error("SMILES string must be at least 3 characters long.");
    return;
  }
  if (!trimmed.includes("*")) {
    antdMessage.error("SMILES string must contain at least one growth point (*).");
    return;
  }
  setState({ loading: true, url: "", preview: false });
  try {
    const [url] = await generateImagesFromSmiles([trimmed]);
    if (!url) throw new Error("no image url");
    setState({ loading: false, url, preview: false });
  } catch {
    antdMessage.error("Could not generate image.");
    setState({ loading: false, url: "", preview: false });
  }
};

/* ------------------------------------------------------------------ */
export default function ScaffoldCanvas({ formData, setFormData, disabled = false }) {
  const [mode, setMode] = useState("");
  const [errors, setErrors] = useState({ scaffold: false, capping_scaffold: false });
  const [img1, setImg1] = useState({ loading: false, url: "", preview: false });
  const [img2, setImg2] = useState({ loading: false, url: "", preview: false });

  // Per-field CDXML undo/redo history — lives here so it survives modal destroyOnClose
  const [editorHistories, setEditorHistories] = useState({
    scaffold: { stack: [], idx: -1 },
    capping_scaffold: { stack: [], idx: -1 },
  });
  // Stable ref so undo/redo handlers read current history without stale closures
  const editorHistoriesRef = useRef(editorHistories);
  useEffect(() => { editorHistoriesRef.current = editorHistories; }, [editorHistories]);

  // CDXML string to programmatically load into ChemDraw (set on open + undo/redo)
  const [cdxmlToLoad, setCdxmlToLoad] = useState(null);

  const [editor, setEditor] = useState({ open: false, fieldKey: "scaffold" });

  // -----------------------------------------------------------------------

  useEffect(() => {
    if (mode) return;
    if (formData.capping_scaffold) {
      setMode("core");
      if (!formData.capping) {
        setFormData((prev) => ({ ...prev, capping: true }));
      }
    } else if (formData.scaffold) {
      setMode("growth");
    }
  }, [formData, mode, setFormData]);

  useEffect(() => {
    if (formData.scaffold && !img1.url && formData.scaffold.includes("*")) {
      getImage(formData.scaffold, setImg1);
    }
    if (formData.capping_scaffold && !img2.url && formData.capping_scaffold.includes("*")) {
      getImage(formData.capping_scaffold, setImg2);
    }
  }, [formData.scaffold, formData.capping_scaffold, img1.url, img2.url]);

  // -----------------------------------------------------------------------

  const handleModeChange = (nextMode) => {
    if (disabled) return;
    setMode(nextMode);
    setFormData((prev) => {
      if (nextMode === "core") return { ...prev, capping: true };
      return { ...prev, capping: false, capping_scaffold: null };
    });
    if (nextMode !== "core") {
      setErrors((prev) => ({ ...prev, capping_scaffold: false }));
      setImg2({ loading: false, url: "", preview: false });
    }
  };

  /**
   * @param {string} key - form field key
   * @param {string} val - new value
   * @param {boolean} preserveHistory - true when the change comes from ChemDraw itself
   *   (we don't want to discard the drawing history in that case)
   */
  const updateField = (key, val, preserveHistory = false) => {
    if (disabled) return;
    setFormData((prev) => ({
      ...prev,
      [key]: val || null,
      ...(key === "capping_scaffold" ? { capping: Boolean(val && String(val).trim()) } : {}),
    }));
    const hasValue = typeof val === "string" && val.trim().length > 0;
    setErrors((e) => ({ ...e, [key]: hasValue && !isValidScaffoldSmiles(val) }));
    if (key === "scaffold") setImg1({ loading: false, url: "", preview: false });
    if (key === "capping_scaffold") setImg2({ loading: false, url: "", preview: false });

    // A manual SMILES edit means any saved CDXML is stale — clear history for that field
    if (!preserveHistory) {
      setEditorHistories((prev) => ({
        ...prev,
        [key]: { stack: [], idx: -1 },
      }));
    }
  };

  // -----------------------------------------------------------------------
  // Editor open / close

  const openEditor = (fieldKey) => {
    const h = editorHistoriesRef.current[fieldKey];
    const latestCdxml = h.idx >= 0 ? h.stack[h.idx] : null;
    setCdxmlToLoad(latestCdxml); // null → ChemDrawCanvas will load SMILES instead
    setEditor({ open: true, fieldKey });
  };

  const closeEditor = () => {
    setEditor((prev) => ({ ...prev, open: false }));
    setCdxmlToLoad(null);
  };

  // -----------------------------------------------------------------------
  // Undo / Redo

  const handleCdxmlChange = (cdxml) => {
    const { fieldKey } = editor;
    setEditorHistories((prev) => {
      const h = prev[fieldKey];
      // Truncate any redo tail, append new state, cap at HISTORY_LIMIT
      const newStack = [...h.stack.slice(0, h.idx + 1), cdxml].slice(-HISTORY_LIMIT);
      return { ...prev, [fieldKey]: { stack: newStack, idx: newStack.length - 1 } };
    });
  };

  const handleUndo = () => {
    const { fieldKey } = editor;
    const h = editorHistoriesRef.current[fieldKey];
    if (h.idx <= 0) return;
    const newIdx = h.idx - 1;
    setCdxmlToLoad(h.stack[newIdx]);
    setEditorHistories((prev) => ({
      ...prev,
      [fieldKey]: { ...prev[fieldKey], idx: newIdx },
    }));
  };

  const handleRedo = () => {
    const { fieldKey } = editor;
    const h = editorHistoriesRef.current[fieldKey];
    if (h.idx >= h.stack.length - 1) return;
    const newIdx = h.idx + 1;
    setCdxmlToLoad(h.stack[newIdx]);
    setEditorHistories((prev) => ({
      ...prev,
      [fieldKey]: { ...prev[fieldKey], idx: newIdx },
    }));
  };

  // -----------------------------------------------------------------------
  /* Re-usable row renderer */
  const renderRow = (label, key, imgState, setImgState, helperTxt, isRequired = false) => (
    <Row gutter={12} align="middle" style={{ marginBottom: 16 }}>
      {/* ① Input */}
      <Col xs={24} md={8}>
        <Space direction="vertical" style={{ width: "100%" }} size={4}>
          <Space>
            <Text strong>{label}</Text>
            {isRequired && <span style={{ color: "red" }}>*</span>}
            {helperTxt && (
              <Tooltip title={helperTxt}>
                <InfoCircleOutlined style={{ color: "rgba(0,0,0,.45)" }} />
              </Tooltip>
            )}
            {errors[key] && (
              <Text type="danger" style={{ fontSize: 12 }}>
                SMILES string must include a growth point (*) and be at least 3 characters long.
              </Text>
            )}
          </Space>

          <Input
            placeholder={`Enter ${label} SMILES`}
            value={formData[key] || ""}
            onChange={(e) => updateField(key, e.target.value)}
            onBlur={(e) => updateField(key, e.target.value)}
            allowClear
            size="middle"
            status={errors[key] ? "error" : undefined}
            disabled={disabled}
          />
        </Space>
      </Col>

      {/* ② Visualise */}
      <Col xs={12} md={3} style={{ paddingTop: "24px" }}>
        <Button
          block
          size="middle"
          icon={<EyeOutlined />}
          onClick={() => getImage(formData[key], setImgState)}
          disabled={disabled}
        >
          Visualise
        </Button>
      </Col>

      {/* ③ Open editor */}
      <Col xs={12} md={3} style={{ paddingTop: "24px" }}>
        <Button
          block
          size="middle"
          icon={<EditOutlined />}
          onClick={() => openEditor(key)}
          disabled={disabled}
        >
          Open Editor
        </Button>
      </Col>

      {/* ④ Image / spinner / placeholder */}
      <Col xs={24} md={10} style={{ textAlign: "center", paddingTop: "24px" }}>
        {imgState.loading ? (
          <Spin size="small" />
        ) : imgState.url ? (
          <AntImage
            src={imgState.url}
            width={240}
            height={240}
            preview={{
              visible: imgState.preview,
              src: imgState.url,
              onVisibleChange: (v) => setImgState((prev) => ({ ...prev, preview: v })),
            }}
            style={{ objectFit: "contain", border: "1px solid #d9d9d9", borderRadius: 4 }}
          />
        ) : (
          <Text type="secondary">No Image</Text>
        )}
      </Col>
    </Row>
  );

  /* ---------------------------------------------------------------- */
  const { fieldKey } = editor;
  const currentHistory = editorHistories[fieldKey] ?? { stack: [], idx: -1 };

  return (
    <>
      {/* ---------- main accordion ---------------------------------- */}
      <Collapse
        bordered={false}
        style={{ marginBottom: 16 }}
        expandIconPosition="end"
        defaultActiveKey={["panel"]}
      >
        <Panel
          key="panel"
          header={<Text style={{ fontWeight: 600, color: "#fff" }}>Enter SMILES</Text>}
          style={{ background: "#003A6C", borderRadius: 4 }}
        >
          <div style={{ padding: 16, background: "#fff", borderRadius: "0 0 4px 4px" }}>
            <Text>
              Select an option, enter SMILES (must include growth points * and be at least 3 characters
              long), then&nbsp;
              <EyeOutlined /> to preview or <EditOutlined /> to draw.
            </Text>

            <Row gutter={12} style={{ margin: "16px 0", alignItems: "center" }}>
              <Col span={6}>
                <Select
                  value={mode || undefined}
                  onChange={handleModeChange}
                  placeholder="SELECT OPTION"
                  style={{ width: "100%" }}
                  suffixIcon={<DownOutlined />}
                  size="middle"
                  disabled={disabled}
                >
                  <Option value="growth">Scaffold Growth / Expansion</Option>
                  <Option value="core">Core Replacement</Option>
                </Select>
              </Col>
              <Col span={18}>
                <Text>
                  Choose your mode above. Then enter your SMILES string and use&nbsp;
                  <EyeOutlined /> to preview or&nbsp;
                  <EditOutlined /> to open the editor.
                </Text>
              </Col>
            </Row>

            {mode === "growth" &&
              renderRow("Scaffold", "scaffold", img1, setImg1, HELPER?.SCAFFOLD, true)}

            {mode === "core" && (
              <>
                {renderRow("Scaffold", "scaffold", img1, setImg1, HELPER?.SCAFFOLD, true)}
                {renderRow("Capping Scaffold", "capping_scaffold", img2, setImg2, HELPER?.CAPPING, true)}
              </>
            )}
          </div>
        </Panel>
      </Collapse>

      {/* ---------------- ChemDraw modal --------------------------- */}
      <Modal
        open={editor.open}
        onCancel={closeEditor}
        footer={null}
        title="ChemDraw Editor"
        width="calc(100vw - 20px)"
        style={{ top: 10 }}
        fullscreen={true}
        bodyStyle={{ padding: 10 }}
        destroyOnClose={true}
      >
        {editor.open && (
          <ChemDrawCanvas
            canvasId={`${fieldKey}-chemdraw`}
            title="Molecule Editor"
            smilesValue={formData[fieldKey]}
            helperText={fieldKey === "scaffold" ? HELPER?.SCAFFOLD : HELPER?.CAPPING}
            // preserveHistory=true so "Get SMILES from Canvas" doesn't wipe undo history
            onChangeSmiles={(val) => updateField(fieldKey, val, true)}
            isModalView
            cdxmlToLoad={cdxmlToLoad}
            onCdxmlChange={handleCdxmlChange}
            canUndo={currentHistory.idx > 0}
            canRedo={currentHistory.idx < currentHistory.stack.length - 1}
            onUndo={handleUndo}
            onRedo={handleRedo}
          />
        )}
      </Modal>
    </>
  );
}

ScaffoldCanvas.propTypes = {
  formData: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};
